// Client/src/tvacDesignFixture.js
// TEMPORARY DESIGN-QA FIXTURE ONLY.
// Purpose: render the real TVAC report template without any evaluator/API call.
// This file must not be used as analytical output or published as a sample report.

export const TVAC_DESIGN_FIXTURE_BRAZIL = {
  payload: {
    text: `DESIGN FIXTURE — NOT ANALYTICAL OUTPUT.

We are a Brazilian agricultural finance company working with midsized grain and input distributors. We purchase short-dated receivables arising from sales to farms and cooperatives, and we currently finance the portfolio through bank facilities and a small number of private investors. Historical payment data exists, but it is held across several originators and has not yet been standardized for external investors.

We are considering a tokenized note or participation structure linked to a diversified pool of receivables. The objective is to broaden access to professional investors, improve reporting on collections and concentration limits, and reduce some manual administration between originators, servicers and investors.

The initial concept is a Brazil-only professional-investor offering with whitelist-based onboarding. Continuous secondary trading is not required. The legal structure, registry model, custody model, transfer process and final operating model remain open.`,
    issuanceJurisdiction: "Brazil",
    saleJurisdictions: ["Brazil"],
    instrumentType: "wrapper",
    custodyModel: "unknown",
    liquidityCadence: "none",
    marketVenue: "unknown",
    settlementModel: "traditional-fiat-rails",
    cashLegModel: "traditional-fiat-rails",
    paymentRails: "traditional-fiat-rails",
    disclosure: "Professional-investor disclosure package to be defined",
    transferRestrictions: "Whitelist; professional investors only; final holding and transfer rules not decided",
    taxNotes: "Tax, withholding and vehicle treatment remain unresolved.",
    investorTypes: { Professional: true, Accredited: false, Retail: false },
    reportLanguageCode: "en",
    reportLanguage: "English",
    caseTitle: "Brazilian Agricultural Receivables Financing"
  },

  assessment: {
    reportLanguageCode: "en",
    reportLanguage: "English",
    case_title: "Brazilian Agricultural Receivables Financing",
    report_case_archetype: "capital_formation",
    verdict: "conditional_go",
    verdict_reason: [
      "The case shows identifiable value pathways through broader professional-investor reach and more structured reporting.",
      "The net margin remains narrow because legal, operational and provider costs are not yet evidenced or allocated.",
      "The token-to-receivables legal link, servicing continuity, transfer controls and investor demand remain open decision gates."
    ],

    factor_scores: {
      new_opportunities: 4,
      cost_savings: 3,
      risk_reduction: 2,
      tokenization_costs: 5,
      new_risks: 3
    },

    added_value: {
      plus: 9,
      minus: 8,
      score: 1,
      formula: "Added Value = (New Opportunities + Cost Savings + Risk Reduction) – (Tokenization Costs + New Risks)"
    },

    input_completeness: {
      score: 92,
      missing_fields: [
        "Final legal structure",
        "Binding provider pricing",
        "Investor-demand evidence",
        "Final custody and registry model"
      ]
    },

    evidence_readiness: {
      level: "Preliminary",
      summary: "The case is sufficiently defined for structured assessment, but several implementation-critical facts remain unverified."
    },

    input_maturity: {
      level: "Structured concept",
      note: "Core commercial objectives are clear; legal and operating design remains open."
    },

    asset_class_context: {
      summary: "The case concerns short-dated Brazilian agricultural receivables financed today through bank facilities and private investors. Tokenization would add a digital representation and controlled investor-access layer rather than change the underlying receivable economics.",
      market_phase: "Structured concept requiring legal, operational and investor validation before implementation.",
      strengths: [
        "Recurring receivable cash flows and historical payment information provide an identifiable financing base.",
        "Professional-investor scope reduces the need for a retail distribution model.",
        "A buy-and-hold structure avoids dependence on continuous secondary liquidity."
      ],
      weaknesses: [
        "Originator data is not yet standardized for external investor reporting.",
        "The final legal vehicle and token-to-receivables rights mapping remain open.",
        "Provider costs and investor demand have not been evidenced."
      ],
      data_confidence: "medium"
    },

    deep_assessment: {
      value_pathways: [
        "Potentially broaden the funding base beyond current bank facilities and a small private-investor group.",
        "Create a more standardized reporting layer for collections, concentrations and portfolio performance.",
        "Potentially reduce selected manual hand-offs if tokenization replaces rather than duplicates existing processes."
      ],
      technical_architecture: [
        "A permissioned issuance and transfer environment is consistent with the stated professional-investor scope.",
        "The authoritative record, registry responsibilities and recovery procedures must be defined before implementation.",
        "Technology choice should follow the legal rights model and operating requirements rather than determine them."
      ],
      legal_regulatory: [
        "The legal classification of the instrument and offering pathway requires independent Brazilian legal analysis.",
        "The relationship between the token, receivables, assignment mechanics, servicing rights and investor claims must be documented.",
        "Transfer restrictions and investor eligibility controls must remain enforceable throughout the lifecycle."
      ],
      market_go_to_market: [
        "The investment proposition depends on professional investors valuing the structure enough to offset additional tokenization costs.",
        "Investor demand should be tested before significant implementation spending.",
        "A narrow Brazil-only initial scope reduces cross-border complexity."
      ],
      operational_risks: [
        "Data quality and reconciliation across originators can undermine reporting reliability.",
        "Servicing continuity and cash-flow controls require clear ownership and fallback procedures.",
        "Provider dependency should be mitigated through service levels, portability and exit arrangements."
      ],
      regulatory_path: [
        "Obtain independent confirmation of classification and the applicable issuance or offering pathway.",
        "Document investor eligibility, disclosure obligations and transfer restrictions.",
        "Validate the legal and operational mapping between receivables, investor rights, registry records and settlement."
      ],
      custody_venue_fit: [
        "The case does not require an open trading venue at the current design stage.",
        "Custody and registry responsibilities remain to be determined.",
        "The selected model should support professional-investor controls, recovery and auditable ownership records."
      ],
      investor_segmentation: [
        "The initial target group is professional investors in Brazil.",
        "The design should not assume retail distribution or unrestricted secondary trading.",
        "Minimum viable distribution should be proven before broadening investor scope."
      ],
      operational_costs: [
        "Legal structuring, data standardization, technology, custody, servicing and compliance costs must be compared with the current financing baseline.",
        "Fixed up-front provider fees can materially weaken initiator economics.",
        "Savings should be counted only where legacy steps are actually removed."
      ],
      settlement_cash_leg_readiness: [
        "Traditional fiat rails are the current working assumption.",
        "No evidence has been supplied of tested end-to-end finality, exception handling or recovery.",
        "Settlement readiness should therefore remain conservative until operating tests are completed."
      ],
      risk_register: [
        {
          risk: "Misaligned incentives / provider capture",
          mitigation: "Use milestone-based commercial terms, portability and explicit exit rights.",
          impact: 4,
          likelihood: 3
        },
        {
          risk: "Investor verification / transfer compliance",
          mitigation: "Define investor eligibility, whitelisting ownership and lifecycle transfer controls.",
          impact: 5,
          likelihood: 3
        },
        {
          risk: "Receivables assignment / enforceability",
          mitigation: "Obtain independent legal analysis of rights mapping, assignment and enforcement.",
          impact: 5,
          likelihood: 3
        },
        {
          risk: "Registry, reporting or cash-flow breakdown",
          mitigation: "Define authoritative records, reconciliation ownership, servicing continuity and recovery.",
          impact: 5,
          likelihood: 3
        },
        {
          risk: "Transfer pathway / operational process weakness",
          mitigation: "Run end-to-end operating tests before implementation and document exception procedures.",
          impact: 5,
          likelihood: 3
        }
      ]
    },

    factor_explanations: {
      new_opportunities: [
        "Professional-investor access could broaden the financing base beyond current bank facilities and private investors.",
        "The Brazil-only initial scope makes the distribution proposition more focused and operationally manageable.",
        "No evidence yet supports a broader or liquid secondary market."
      ],
      cost_savings: [
        "Standardized reporting and fewer manual hand-offs could reduce selected administration costs.",
        "Savings remain conditional on retiring legacy processes rather than running tokenized and conventional workflows in parallel."
      ],
      risk_reduction: [
        "A controlled registry and standardized reporting could improve traceability and reconciliation.",
        "The case does not yet demonstrate material reduction of credit, servicing or legal risk."
      ],
      tokenization_costs: [
        "Legal structuring, provider integration, data standardization, custody and compliance introduce meaningful up-front and ongoing costs.",
        "Binding provider pricing is not yet available.",
        "The initiator currently bears much of the unresolved cost burden."
      ],
      new_risks: [
        "The legal link between token and receivables remains unresolved.",
        "Operational dependency on registry, technology, custody and servicing providers introduces new failure modes.",
        "Transfer controls and recovery procedures are not yet tested."
      ]
    },

    recommendation: [
      `How to Improve Added Value in This Case

The current case has identifiable financing and reporting benefits, but initiator value is constrained by unresolved cost allocation, distribution evidence and legal-operational design.

1. Rebalance cost and risk allocation away from the Asset Owner
Required restructuring:
- Link material provider fees to delivery milestones and validated outcomes.
- Require portability, service levels and exit rights.
Expected effect: lower TC and NR if provider economics and failure responsibilities are better controlled.

2. Treat distribution and liquidity as a design requirement, not an assumption
Required restructuring:
- Validate professional-investor demand before material build-out.
- Design transfer controls for the actual investor scope rather than an assumed liquid market.
Expected effect: strengthen NO and reduce NR by aligning distribution with evidenced demand.

3. Simplify jurisdictional and investor scope in the initial phase
Required restructuring:
- Keep the first implementation Brazil-only and professional-investor focused unless evidence supports expansion.
- Avoid unnecessary cross-border and retail complexity.
Expected effect: reduce TC and NR while preserving the core NO pathway.

4. Align token design more tightly with monetisable rights
Required restructuring:
- Define the token's legal relationship to receivables, collections, registry records and investor claims.
- Ensure the operating model supports enforceability, servicing continuity and transparent cash-flow controls.
Expected effect: strengthen RR and reduce NR.

Conclusion:
Under revised assumptions and stronger evidence, the case could shift from Conditionally Viable to Viable.`
    ],

    next_steps: [
      "Obtain independent confirmation of legal classification and the applicable Brazilian offering pathway.",
      "Document the legal and operational mapping between token, receivables, investor rights and cash flows.",
      "Validate full provider pricing and compare it with the conventional financing baseline.",
      "Document professional-investor demand before committing to material implementation spend.",
      "Complete end-to-end testing of registry, transfer, servicing, reconciliation and settlement controls."
    ],

    assumptions: [
      "The initial scope remains Brazil-only and limited to professional investors.",
      "Continuous secondary liquidity is not required for the initial commercial proposition.",
      "Traditional fiat payment rails remain available for settlement.",
      "Tokenization is implemented only where it replaces or materially improves existing workflow steps."
    ],

    sources: [
      "User-supplied case information (design fixture)",
      "Verified regulatory context used by the TVAC Brazil profile (not reproduced as legal advice)"
    ],

    what_if_scenarios: [
      "If verified investor demand is materially stronger than assumed and provider pricing falls, New Opportunities may improve while Tokenization Costs decline.",
      "If tokenization duplicates existing reporting and servicing processes rather than replacing them, Cost Savings may fall and Tokenization Costs may rise."
    ],

    future_scenarios: [
      "If standardized digital asset infrastructure becomes more widely available to the target professional-investor market, some infrastructure costs could decline.",
      "If applicable legal or operational requirements become more complex, implementation costs and new risks could increase."
    ],

    stakeholder_added_value: {
      initiator: {
        opportunities: ["Broader professional-investor reach", "More structured portfolio reporting"],
        costs_risks: ["Structuring and provider costs", "Legal and operational responsibility"],
        net: "Conditional"
      },
      investors: {
        opportunities: ["Improved reporting", "Controlled digital ownership and transfer records"],
        costs_risks: ["Reliance on servicing, registry and legal enforceability"],
        net: "Conditional"
      },
      infrastructure_providers: {
        opportunities: ["New service revenue", "Potential repeatable infrastructure"],
        costs_risks: ["Service-level and recovery obligations"],
        net: "Positive"
      }
    },

    report_export_token: null,

    _telemetry: {
      model: "STATIC DESIGN FIXTURE",
      mode: "design_qa_only",
      evaluator: "none",
      note: "No evaluator or OpenAI API call was used."
    }
  }
};
